import React, { useEffect, useState } from "react";
import { NavLink, Navigate, Outlet, useLocation } from "react-router-dom";
import { useUsmAuth } from "@/contexts/UsmAuthContext";
import { useUsmPrefs } from "@/contexts/UsmPrefsContext";
import usmApi from "@/lib/usmApi";
import { LayoutDashboard, Building2, Users, ScrollText, LogOut, ChevronRight, ChevronDown, Search, Tag, ShoppingCart, Receipt, Truck, Banknote, StickyNote, Menu, X, Maximize2, Minimize2, Gem, Coins, Wallet, Watch, Database, Settings as SettingsIcon, Package, Star, Clock } from "lucide-react";
import FavoriteStar from "./hub/FavoriteStar";
import { PAGE_INDEX, useFavorites, useRecents, pushRecent } from "./hub/usmSidebarPrefs";
import { MessengerProvider } from "@/contexts/MessengerContext";
import MessengerFAB from "@/components/messenger/MessengerFAB";
import useUsmFreshnessKey from "@/lib/useUsmFreshnessKey";

const NAV = [
  { to: "/usm",          end: true, label: "Panel",       icon: LayoutDashboard, perm: null },
  // Phase 54.24.1 — Ürün Satış + Fiyat Sorgu collapse into one entry
  // ("Ürün Satış") backed by the HubTabs component on both pages.
  // Sidebar entry visible when the user has either permission — the
  // tab strip then hides tabs the user cannot see.
  { to: "/usm/pos",      label: "Ürün Satış",  icon: ShoppingCart,    permAny: ["pos.access", "price_lookup.use"] },
  { to: "/usm/sales",    label: "Satışlar",   icon: Receipt,         perm: "sales.view" },
  // Phase 53.4 — Kasa Kapanış migrated into Muhasebe (see Accounting tabs).
  { to: "/usm/transfers", label: "Transferler", icon: Truck,         perm: "transfers.view" },
  // Phase 54.23.8 + 54.25 — Sidebar simplification. Şubeler /
  // Çalışanlar / Etiket Şablonları / Notlar collapse into a single
  // "Şubeler" entry backed by HubTabs. Notlar has no permission gate
  // (perm: null) so this sidebar entry is visible to every operator —
  // the HubTabs strip then hides tabs the user lacks perms for.
  { to: "/usm/branches", label: "Şubeler", icon: Building2, perm: null },
  // Phase 54.1 — Sidebar cleanup: three flat inventory entries collapse
  // into a single expandable Envanter group. Children (Gümüş / Altın /
  // Saat) keep their existing icons + routes so nothing else changes.
  // Future workspaces (Elmas, Külçe, Hediye, …) simply append here.
  {
    kind: "group",
    key: "inventory",
    label: "Envanter",
    icon: Package,
    perm: "inventory.view",
    children: [
      { to: "/usm/inventory/silver", label: "Gümüş Envanteri", icon: Gem,   perm: "inventory.view" },
      { to: "/usm/inventory/gold",   label: "Altın Envanteri",  icon: Coins, perm: "inventory.view" },
      { to: "/usm/inventory/watch",  label: "Saat Envanteri",   icon: Watch, perm: "inventory.view" },
    ],
  },
  // Phase 53.1 — Muhasebe (Accounting) ERP foundation.
  { to: "/usm/accounting", label: "Muhasebe", icon: Wallet, perm: "accounting.view" },
  // Phase 54.23.8 — Denetim + Yedekleme & Kurtarma collapse into a
  // single "Denetim" sidebar entry. HubTabs inside handles both
  // tabs; sidebar entry visible when the user has audit.view OR is
  // super admin (matches the previous flat entries' visibility).
  { to: "/usm/audit", label: "Denetim", icon: ScrollText, permAny: ["audit.view"], showIfSuperAdmin: true },
];

export default function UsmLayout() {
  const { employee, branch, loading, logout, hasPerm } = useUsmAuth();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  useEffect(() => { setSidebarOpen(false); }, [location.pathname]);

  // Global freshness signal. Increments only on real user/OS events
  // (tab visible after ≥15 s hidden, focus after ≥15 s blur, network
  // online) — never on a timer. Applied as a `key` on the route
  // <Outlet /> so the CURRENT ROUTE remounts, refetching data with
  // the same authenticated axios instance. Layout, auth, messenger
  // WS, permissions all stay alive because they live above the key.
  const freshnessKey = useUsmFreshnessKey();

  // Phase 54.23.9 — track recent pages. Pure client-side. Pushes
  // every visited pathname (including the current one). The
  // storage layer holds MAX_REC + 1 entries so that the render
  // layer can safely exclude the current pathname and still show
  // MAX_REC=3 non-current entries. This is more robust than a
  // ref-based "previous pathname" scheme, which resets on any
  // full page reload (bookmark, hard refresh, etc.).
  useEffect(() => { pushRecent(location.pathname); }, [location.pathname]);

  // Phase 54.23.8 — Nav visibility helper. Supports the existing
  // `perm` (single required permission), `superOnly` (super admin
  // only), and the new merged-hub attributes `permAny` (array —
  // visible if user has ANY listed permission) and
  // `showIfSuperAdmin` (super admin bypasses permAny). Zero
  // permission drift: the effective visibility for every existing
  // entry is identical to Phase 54.0.
  const canSee = React.useCallback((n) => {
    if (n.section || n.kind === "group") return true;  // handled elsewhere
    const isSuper = employee?.role === "super_admin";
    if (n.superOnly) return isSuper;
    if (n.permAny && n.permAny.length) {
      if (n.showIfSuperAdmin && isSuper) return true;
      return n.permAny.some((p) => hasPerm(p));
    }
    if (n.perm) return hasPerm(n.perm);
    return true;
  }, [employee?.role, hasPerm]);

  // Phase 47.2 #4-6 + Phase 48 #3-4 + Hotfix — Global Tablet Mode.
  //
  // Design principle: **Tablet Mode and browser Fullscreen are independent
  // concepts.** Fullscreen is a best-effort bonus we request when the user
  // taps the toggle; Tablet Mode is a pure UI state driven only by the user.
  //
  // We deliberately do NOT listen to `fullscreenchange` to auto-exit Tablet
  // Mode. Older versions did that, which caused iPad Safari (which doesn't
  // implement `requestFullscreen` on `<html>`) and Android Chrome (which
  // exits fullscreen when the URL bar reveals during scroll or route change)
  // to silently drop out of Tablet Mode on random page transitions. The
  // user has zero reason to lose their large-touch-target layout just
  // because the OS decided to show the status bar.
  //
  // Result: Tablet Mode only exits when the user explicitly taps "Çık".
  // ESC on desktop still exits fullscreen but the CSS class stays until the
  // user chooses to leave (correct: they may want giant buttons even in a
  // window that isn't fullscreen).
  const [tablet, setTablet] = useState(() => {
    try { return sessionStorage.getItem("usm_tablet_mode") === "1"; } catch { return false; }
  });
  useEffect(() => {
    const root = document.documentElement;
    if (tablet) root.classList.add("usm-tablet"); else root.classList.remove("usm-tablet");
    try { sessionStorage.setItem("usm_tablet_mode", tablet ? "1" : "0"); } catch { /* private mode */ }
  }, [tablet]);
  // Phase 50 #8 — enforce the CSS class on EVERY route change. iPad Safari
  // sometimes drops class-list changes across navigation (or a third-party
  // interaction wipes it), leaving the "Çık" button on but the UI back to
  // desktop mode. We re-apply on every location change while `tablet` is on.
  useEffect(() => {
    const root = document.documentElement;
    if (tablet && !root.classList.contains("usm-tablet")) {
      root.classList.add("usm-tablet");
    } else if (!tablet && root.classList.contains("usm-tablet")) {
      root.classList.remove("usm-tablet");
    }
  }, [tablet, location.pathname]);
  const toggleTablet = async () => {
    try {
      if (!tablet) {
        // Best-effort: request browser fullscreen. iPad Safari lacks
        // `requestFullscreen` on <html>; we don't care and set Tablet Mode
        // regardless because that's the actual UX the user asked for.
        const el = document.documentElement;
        if (el.requestFullscreen) {
          try { await el.requestFullscreen(); } catch { /* denied / not supported */ }
        }
        setTablet(true);
      } else {
        // Exit any active fullscreen quietly, then leave Tablet Mode.
        if (document.exitFullscreen && document.fullscreenElement) {
          try { await document.exitFullscreen(); } catch { /* already gone */ }
        }
        setTablet(false);
      }
    } catch { /* noop — never let the toggle throw */ }
  };

  if (loading) return <div className="min-h-screen bg-[#0A0A0A]" />;
  if (!employee) return <Navigate to="/usm/login" replace state={{ from: location.pathname }} />;

  return (
    <MessengerProvider currentUserId={employee?.id}>
    <div data-testid="usm-layout" className="min-h-screen bg-[#F5F5F4] text-[#0A0A0A]">
      {/* Phase 47.2 #4 + Phase 48 #4 — Global Tablet-Mode toggle. Fixed
          top-right on every page. Respects iOS safe area so it never
          overlaps the Dynamic Island, notch, or status-bar icons. */}
      <button
        data-testid="usm-tablet-toggle"
        onClick={toggleTablet}
        title={tablet ? "Tablet Modundan Çık" : "Tablet Modu"}
        aria-label="Tablet Mode"
        style={{
          top: "calc(env(safe-area-inset-top, 0px) + 16px)",
          right: "calc(env(safe-area-inset-right, 0px) + 14px)",
        }}
        className={
          "fixed z-[60] inline-flex items-center gap-1.5 rounded-full border px-3 py-2 text-[10px] uppercase tracking-[0.2em] shadow-md backdrop-blur-sm transition min-h-[36px] " +
          (tablet
            ? "bg-[#C8A96A] text-white border-[#C8A96A] hover:bg-[#B7955A]"
            : "bg-white/95 text-[#0A0A0A] border-[#E7E5E4] hover:bg-white")
        }
      >
        {tablet ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
        <span className="hidden sm:inline">{tablet ? "Çık" : "Tablet"}</span>
      </button>
      {/* Phase 46 Feature #5 — Tablet/iPad top bar with hamburger */}
      <div className="lg:hidden fixed top-0 inset-x-0 h-12 bg-[#0A0A0A] text-white flex items-center justify-between px-4 z-50">
        <button data-testid="usm-menu-toggle" onClick={() => setSidebarOpen((v) => !v)} className="p-1.5">
          {sidebarOpen ? <X size={18} /> : <Menu size={18} />}
        </button>
        <p className="text-[10px] uppercase tracking-[0.3em] text-[#C8A96A]">Uzungöl Kuyumculuk · USM</p>
        <div className="w-6" />
      </div>

      {/* Sidebar — hidden in Tablet Mode (Phase 50 #4 replaces it with Bottom Nav). */}
      <aside className={
        "fixed left-0 top-0 bottom-0 w-60 bg-[#0A0A0A] text-white flex flex-col z-40 transition-transform " +
        (tablet ? "-translate-x-full" : ((sidebarOpen ? "translate-x-0" : "-translate-x-full") + " lg:translate-x-0"))
      }>
        <div className="px-6 py-6 border-b border-white/10">
          <p className="text-[10px] uppercase tracking-[0.4em] text-[#C8A96A]">Uzungöl Kuyumculuk</p>
          <p className="font-heading text-lg mt-0.5">Handmade Jewelry</p>
          <p className="text-[9px] uppercase tracking-[0.35em] text-[#71717A] mt-1">Store Manager</p>
        </div>
        <nav className="flex-1 py-3 px-3 space-y-0.5 overflow-y-auto">
          {NAV.filter((n) => n.section || n.kind === "group" || canSee(n)).map((n, idx) => {
            if (n.section) {
              return (
                <div key={`sec-${idx}`} className="pt-3 pb-1 px-3 text-[9px] uppercase tracking-[0.35em] text-[#52525B] first:pt-0">
                  {n.section}
                </div>
              );
            }
            if (n.kind === "group") {
              const children = (n.children || []).filter((c) => (!c.perm || hasPerm(c.perm)) && (!c.superOnly || employee.role === "super_admin"));
              if (children.length === 0) return null;
              return <NavGroup key={`grp-${n.key}`} item={n} items={children} pathname={location.pathname} />;
            }
            const Icon = n.icon;
            return (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                data-testid={`usm-nav-${n.to === "/usm" ? "dashboard" : n.to.split("/").pop()}`}
                className={({ isActive }) =>
                  "flex items-center gap-3 px-3 py-2.5 text-sm rounded transition-colors " +
                  (isActive
                    ? "bg-[#C8A96A] text-white"
                    : "text-[#A1A1AA] hover:bg-white/5 hover:text-white")
                }
              >
                <Icon size={16} strokeWidth={1.6} />
                <span className="uppercase tracking-[0.12em] text-[12px] font-medium">{n.label}</span>
              </NavLink>
            );
          })}
          {/* Phase 54.23.9 — Favorites & Recent Pages. Compact
              client-side sections. Auto-hide when empty so a fresh
              operator sees zero extra chrome. */}
          <FavoritesAndRecents />
        </nav>
        <BranchSwitcher />
        <div className="border-t border-white/10 px-3 py-3">
          <div className="px-3 py-2 text-[11px] text-[#A1A1AA]">
            <p className="text-white truncate" data-testid="usm-current-user">{employee.full_name || employee.username}</p>
            <p className="uppercase tracking-[0.15em] mt-0.5 text-[10px]">
              {ROLE_LABEL[employee.role] || employee.role}{branch ? ` · ${branch.name}` : ""}
            </p>
          </div>
          <button
            data-testid="usm-logout-btn"
            onClick={logout}
            className="w-full flex items-center gap-3 px-3 py-2.5 text-sm rounded hover:bg-white/5 text-[#A1A1AA] hover:text-white"
          >
            <LogOut size={14} />
            <span className="uppercase tracking-[0.12em] text-[12px]">Çıkış</span>
          </button>
        </div>
      </aside>

      {sidebarOpen && (
        <div className="lg:hidden fixed inset-0 bg-black/40 z-30" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Main */}
      <main className={"min-h-screen pt-12 lg:pt-0 " + (tablet ? "lg:ml-0 pb-24" : "lg:ml-60")}>
        <div className={"px-4 sm:px-6 lg:px-8 py-5 lg:py-7 " + (tablet ? "max-w-full" : "max-w-full lg:max-w-[1400px]")}>
          {/* key = route + freshness signal. Route change remounts as
              before; a freshness bump additionally remounts the
              current route so its mount-time fetches re-run. */}
          <Outlet key={`${location.pathname}::${freshnessKey}`} />
        </div>
      </main>

      {/* Phase 50 #4 — Bottom Navigation for Tablet Mode. Modern POS style
          with icon-above-label, horizontally scrollable, active-gold pill.
          Only rendered when Tablet Mode is on; desktop layout unchanged. */}
      {tablet && (
        <BottomNav navItems={NAV.flatMap((n) => n.kind === "group" ? (n.children || []) : (n.section ? [] : [n])).filter(canSee)} />
      )}

      {/* Phase 55 — Messenger V1 floating action button. Visible on every
          ERP page, above the tablet bottom nav. Lazy-loads the panel. */}
      <MessengerFAB />
    </div>
    </MessengerProvider>
  );
}

// Phase 54.1 — Collapsible sidebar group. Used for the Envanter menu but
// generic so future groups (Diamond / Bullion / Gift inventory) can plug
// in by appending to the item's `children` array. The group auto-expands
// when the current route matches any child so the operator never loses
// context after a page navigation.
function NavGroup({ item, items, pathname }) {
  const Icon = item.icon;
  const storageKey = `usm-nav-group-${item.key}`;
  const anyChildActive = items.some((c) => pathname.startsWith(c.to));
  const [open, setOpen] = useState(() => {
    if (anyChildActive) return true;
    try {
      const v = window.localStorage.getItem(storageKey);
      if (v === "1") return true;
      if (v === "0") return false;
    } catch { /* ignore */ }
    return true; // default open on first visit so operators discover items
  });
  useEffect(() => {
    if (anyChildActive) setOpen(true);
  }, [anyChildActive]);
  const toggle = () => {
    setOpen((prev) => {
      const next = !prev;
      try { window.localStorage.setItem(storageKey, next ? "1" : "0"); } catch { /* ignore */ }
      return next;
    });
  };
  return (
    <div data-testid={`usm-nav-group-${item.key}`}>
      <button
        data-testid={`usm-nav-group-${item.key}-toggle`}
        onClick={toggle}
        aria-expanded={open}
        className={
          "w-full flex items-center gap-3 px-3 py-2.5 text-sm rounded transition-colors " +
          (anyChildActive ? "text-white" : "text-[#A1A1AA] hover:bg-white/5 hover:text-white")
        }
      >
        <Icon size={16} strokeWidth={1.6} />
        <span className="flex-1 text-left uppercase tracking-[0.12em] text-[12px] font-medium">{item.label}</span>
        {open ? <ChevronDown size={14} className="opacity-70" /> : <ChevronRight size={14} className="opacity-70" />}
      </button>
      {open && (
        <div className="ml-2 mt-0.5 mb-1 border-l border-white/10 pl-2 space-y-0.5">
          {items.map((c) => {
            const ChildIcon = c.icon;
            return (
              <NavLink
                key={c.to}
                to={c.to}
                data-testid={`usm-nav-${c.to.split("/").pop()}`}
                className={({ isActive }) =>
                  "flex items-center gap-3 px-3 py-2 text-sm rounded transition-colors " +
                  (isActive
                    ? "bg-[#C8A96A] text-white"
                    : "text-[#A1A1AA] hover:bg-white/5 hover:text-white")
                }
              >
                <ChildIcon size={14} strokeWidth={1.6} />
                <span className="uppercase tracking-[0.12em] text-[11.5px] font-medium">{c.label}</span>
              </NavLink>
            );
          })}
        </div>
      )}
    </div>
  );
}


// Phase 46 Feature #6 — Sticky per-user branch switcher.
function BranchSwitcher() {
  const { employee, branch } = useUsmAuth();
  const { prefs, setActiveBranch } = useUsmPrefs();
  const [branches, setBranches] = useState([]);
  useEffect(() => {
    (async () => {
      try {
        const r = await usmApi.get("/branches");
        setBranches(Array.isArray(r.data) ? r.data : (r.data?.items || []));
      } catch { /* keep empty */ }
    })();
  }, []);
  const isAdmin = employee?.role === "super_admin";
  const activeId = prefs.active_branch_id || employee?.branch_id || branch?.id;
  const allowed = isAdmin ? branches : branches.filter((b) => (employee?.branch_ids || [employee?.branch_id]).includes(b.id));
  if (allowed.length <= 1) return null;
  return (
    <div className="px-3 py-3 border-t border-white/10">
      <p className="px-3 pb-1.5 text-[9px] uppercase tracking-[0.28em] text-[#A1A1AA]">Aktif Şube</p>
      <select
        data-testid="usm-branch-switcher"
        value={activeId || ""}
        onChange={(e) => setActiveBranch(e.target.value)}
        className="w-full bg-white/5 border border-white/10 text-white text-xs px-3 py-2 rounded outline-none focus:border-[#C8A96A]"
      >
        {allowed.map((b) => (
          <option key={b.id} value={b.id} className="bg-[#0A0A0A]">{b.name}{b.code ? ` · ${b.code}` : ""}</option>
        ))}
      </select>
    </div>
  );
}

const ROLE_LABEL = {
  super_admin: "Süper Yönetici",
  branch_manager: "Şube Müdürü",
  sales_employee: "Satış Personeli",
};

export function PageHeader({ title, breadcrumbs = [], actions = null }) {
  return (
    <div className="mb-7">
      {breadcrumbs.length > 0 && (
        <nav className="flex items-center gap-1 text-[11px] uppercase tracking-[0.2em] text-[#71717A] mb-3">
          {breadcrumbs.map((b, i) => (
            <span key={i} className="flex items-center gap-1">
              {b.to ? (
                <NavLink to={b.to} className="hover:text-[#0A0A0A]">{b.label}</NavLink>
              ) : (
                <span>{b.label}</span>
              )}
              {i < breadcrumbs.length - 1 && <ChevronRight size={11} className="mt-px" />}
            </span>
          ))}
        </nav>
      )}
      <div className="flex flex-wrap items-end justify-between gap-4">
        {/* Phase 54.23.9 — Star toggle sits inline next to the title
            so it's always in reach without disturbing the actions
            slot. Auto-hides on routes not in PAGE_INDEX. */}
        <div className="flex items-center gap-2 min-w-0">
          <h1 className="font-heading text-3xl truncate">{title}</h1>
          <FavoriteStar />
        </div>
        {actions}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Phase 54.23.9 — Sidebar Favorites & Recent Pages.
// Compact, elegant, auto-hides when the operator has nothing
// pinned or hasn't navigated yet. Uses the same visual language
// as the main NAV rows (13 px icon, 11 px uppercase 0.12em label)
// but tightened to py-1.5 so five pinned rows fit without pushing
// the branch switcher off-screen on a 13" laptop.
// ─────────────────────────────────────────────────────────────
function FavoritesAndRecents() {
  const favorites = useFavorites();
  const recents = useRecents();
  const location = useLocation();

  // Recents exclude the current pathname — otherwise the top entry
  // is always "the page you're on", which is useless clutter. Store
  // is capped at MAX_REC+1 in usmSidebarPrefs.js so the .slice(0,3)
  // below always yields the intended 3 entries even when the current
  // page happens to occupy one of the stored slots.
  const recentEntries = recents.filter((p) => p !== location.pathname).slice(0, 3);

  if (favorites.length === 0 && recentEntries.length === 0) return null;

  return (
    <>
      {favorites.length > 0 && (
        <div data-testid="sidebar-favorites" className="pt-3">
          <div className="pb-1 px-3 text-[9px] uppercase tracking-[0.35em] text-[#52525B] inline-flex items-center gap-1.5">
            <Star size={9} strokeWidth={2} />
            <span>Favoriler</span>
          </div>
          {favorites.map((p) => <SidebarShortcut key={`fav-${p}`} pathname={p} testid={`sidebar-fav-${p.split("/").pop() || "dashboard"}`} />)}
        </div>
      )}
      {recentEntries.length > 0 && (
        <div data-testid="sidebar-recents" className="pt-3">
          <div className="pb-1 px-3 text-[9px] uppercase tracking-[0.35em] text-[#52525B] inline-flex items-center gap-1.5">
            <Clock size={9} strokeWidth={2} />
            <span>Son Ziyaret</span>
          </div>
          {recentEntries.map((p) => <SidebarShortcut key={`rec-${p}`} pathname={p} testid={`sidebar-rec-${p.split("/").pop() || "dashboard"}`} />)}
        </div>
      )}
    </>
  );
}

function SidebarShortcut({ pathname, testid }) {
  const meta = PAGE_INDEX[pathname];
  if (!meta) return null;  // stale localStorage entry — defensive.
  const Icon = meta.icon;
  return (
    <NavLink
      to={pathname}
      end
      data-testid={testid}
      className={({ isActive }) =>
        "flex items-center gap-3 px-3 py-1.5 text-sm rounded transition-colors " +
        (isActive
          ? "bg-[#C8A96A] text-white"
          : "text-[#A1A1AA] hover:bg-white/5 hover:text-white")
      }
    >
      <Icon size={13} strokeWidth={1.6} />
      <span className="uppercase tracking-[0.12em] text-[11.5px] font-medium truncate">{meta.label}</span>
    </NavLink>
  );
}

// Phase 50 #4 — Bottom Navigation used only in Tablet Mode. Modern POS style:
// icons above short labels, active tab tinted gold, horizontally scrollable
// (all 10+ items fit on desktop but iPad portrait needs to scroll). Respects
// the iOS home-bar safe area.
function BottomNav({ navItems }) {
  return (
    <nav
      data-testid="usm-bottom-nav"
      style={{ paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 6px)" }}
      className="fixed left-0 right-0 bottom-0 z-40 bg-[#0A0A0A] border-t border-white/10 shadow-[0_-4px_20px_rgba(0,0,0,0.25)]"
    >
      <div className="flex overflow-x-auto scrollbar-hidden">
        {navItems.map((n) => {
          const Icon = n.icon;
          const key = n.to === "/usm" ? "dashboard" : n.to.split("/").pop();
          return (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={`usm-btmnav-${key}`}
              className={({ isActive }) =>
                "flex flex-col items-center justify-center gap-1 min-w-[92px] px-3 py-2.5 text-[10px] uppercase tracking-[0.12em] transition-colors " +
                (isActive
                  ? "text-[#C8A96A] border-t-2 border-[#C8A96A] -mt-[2px] bg-white/[0.03]"
                  : "text-[#A1A1AA] hover:text-white border-t-2 border-transparent -mt-[2px]")
              }
            >
              <Icon size={20} />
              <span className="truncate max-w-full leading-tight">{n.label}</span>
            </NavLink>
          );
        })}
      </div>
    </nav>
  );
}

